import { create } from 'zustand';
import { Client } from '../types';
import clientService from '../services/clientService';
import { extractErrorMessage } from '../utils/errorUtils';

interface ClientStore {
  clients: Client[];
  isLoading: boolean;
  error: string | null;
  loadClients: () => Promise<void>;
  addClient: (client: Omit<Client, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Client>;
  updateClient: (id: number, client: Partial<Client>) => Promise<void>;
  deleteClient: (id: number) => Promise<void>;
  getClientById: (id: number) => Client | undefined;
  searchClients: (query: string) => Promise<void>;
  clearClients: () => void;
}

export const useClientStore = create<ClientStore>((set, get) => ({
  clients: [],
  isLoading: false,
  error: null,

  // Charger tous les clients depuis l'API
  loadClients: async () => {
    set({ isLoading: true, error: null });
    try {
      const clients = await clientService.getAll();
      set({ clients, isLoading: false });
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Erreur lors du chargement des clients');
      set({ error: errorMessage, isLoading: false });
    }
  },

  // Ajouter un client via l'API
  addClient: async (clientData) => {
    set({ isLoading: true, error: null });
    try {
      const newClient = await clientService.create(clientData);
      set((state) => ({
        clients: [...state.clients, newClient],
        isLoading: false,
      }));
      return newClient;
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Erreur lors de la création du client');
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  // Mettre à jour un client via l'API
  updateClient: async (id, clientData) => {
    set({ isLoading: true, error: null });
    try {
      const updatedClient = await clientService.update(id, clientData);
      set((state) => ({
        clients: state.clients.map((client) =>
          client.id === id ? updatedClient : client
        ),
        isLoading: false,
      }));
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Erreur lors de la mise à jour du client');
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  // Supprimer un client via l'API
  deleteClient: async (id) => {
    set({ isLoading: true, error: null });
    try {
      await clientService.delete(id);
      set((state) => ({
        clients: state.clients.filter((client) => client.id !== id),
        isLoading: false,
      }));
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Erreur lors de la suppression du client');
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  // Récupérer un client par ID (depuis le state local)
  getClientById: (id) => {
    return get().clients.find((client) => client.id === id);
  },

  // Rechercher des clients via l'API
  searchClients: async (query) => {
    set({ isLoading: true, error: null });
    try {
      const clients = await clientService.search(query);
      set({ clients, isLoading: false });
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Erreur lors de la recherche de clients');
      set({ error: errorMessage, isLoading: false });
    }
  },

  // Vider tous les clients (lors du logout)
  clearClients: () => {
    set({ clients: [], isLoading: false, error: null });
  },
}));